import { FC, useEffect, useState } from 'react';
import { Modal } from 'vopay';
import ElinxConnect from '@/components/ElinxConnect/ElinxConnect';
import AddInteracModal, { type FormValues as AddInteracFormValues } from '@/components/Customer/AddInteracModal';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import usePaymentMethods from '@/hooks/usePaymentMethods';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import Options, { type OptionPaymentMethod } from './Options';

type ProvidePaymentMethodProps = {
  isOpen: boolean;
  accountID: string;
  accountName: string;
  omit?: OptionPaymentMethod[];
  onClose: () => void;
  onAddInterac: (token: string, eTransferMethod: AddInteracFormValues['eTransferMethod']) => void;
  onBankAdded: (token: string) => void;
  onCreditCardAdded: (token: string) => void;
  onDebitCardAdded: (token: string) => void;
};

type ElinxConnectMethod = 'bank' | 'credit' | 'debitcard';

const ProvidePaymentMethod: FC<ProvidePaymentMethodProps> = ({
  isOpen: showingPaymentMethod,
  onClose,
  accountID,
  accountName,
  omit = [],
  onAddInterac,
  onBankAdded,
  onCreditCardAdded,
  onDebitCardAdded,
}) => {
  const [methodType, setMethodType] = useState<ElinxConnectMethod | null>(null);
  const [showingInteracForm, setShowingInteracForm] = useState(false);
  const [isProvidingDetails, setIsProvidingDetails] = useState(false);
  const { response } = useClientData();
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.ProvidePaymentMethod' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { addInterac } = usePaymentMethods({
    accountID: accountID,
  });

  const handleProvideDetails = (method: ElinxConnectMethod) => {
    setMethodType(method);
    setIsProvidingDetails(true);
  };

  useEffect(() => {
    if (!showingPaymentMethod) setShowingInteracForm(false);
  }, [showingPaymentMethod]);

  function handleSelect(type: OptionPaymentMethod) {
    if (type === 'Email') {
      setShowingInteracForm(true);
    } else {
      const map: Record<Exclude<OptionPaymentMethod, 'Email'>, ElinxConnectMethod> = {
        BankAccount: 'bank',
        CreditCard: 'credit',
        DebitCard: 'debitcard',
      };

      handleProvideDetails(map[type]);
    }
  }

  const handleAddInterac = async (params: AddInteracFormValues) => {
    const addResponse = await addInterac({
      ...params,
      recipientName: accountName,
    });

    if (!addResponse.Success) {
      await alertDialog.error(
        t('Dialog.AddMethod.error', { defaultValue: 'Something went wrong trying to add your payment method.' }),
        null,
        {
          abortLabel: tGlobal('Buttons.back'),
        },
      );

      return;
    }

    onAddInterac(addResponse.Token, params.eTransferMethod);
  };

  const content = (
    <>
      <Modal isVisible={showingPaymentMethod} enableBgClick={false}>
        <Options omit={omit} onClose={onClose} onSelect={handleSelect} />
      </Modal>

      <AddInteracModal
        isOpen={showingInteracForm}
        onClose={() => setShowingInteracForm(false)}
        onSubmit={handleAddInterac}
      />

      {isProvidingDetails && (
        <ElinxConnect
          setting={{
            RedirectURL: 'https://vopay.com',
            Country: response.Country,
            PaymentSelectionMethod: methodType as string,
            ClientControlled: true,
            RedirectMethod: 'JavascriptMessage',
            ClientAccountID: accountID,
          }}
          onClose={() => setIsProvidingDetails(false)}
          onSuccess={(event) => {
            onClose();
            setIsProvidingDetails(false);

            if (methodType === 'bank') {
              onBankAdded(event.data.Token);
            }

            if (methodType === 'credit') {
              onCreditCardAdded(event.data.Token);
            }

            if (methodType === 'debitcard') {
              onDebitCardAdded(event.data.Token);
            }
          }}
        />
      )}
    </>
  );

  return createPortal(content, document.getElementById('modal-root')!);
};

export default ProvidePaymentMethod;
